import styled from 'styled-components'
import React, { useState, useEffect, useRef } from 'react'

interface GridData {
    title: string
    value: number
    __uom: string
    color?: string
    titleFontSize?: number
    valueFontSize?: number
    uomFontSize?: number
    titleBold?: boolean
    valueBold?: boolean
    uomBold?: boolean
}

interface GridProps {
    columns?: number
    rows?: number
    data: GridData[]
}

const GridContainer = styled.div<{ rows?: number }>`
    height: 100%;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 5px;
        height: 10px;
        -webkit-border-radius: 16px;
    }

    &::-webkit-scrollbar-track-piece {
        background-color: var(--gray-100);
        -webkit-border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:vertical {
        height: 5px;
        background-color: var(--gray-600);
        -webkit-border-radius: 3px;
    }
`

const Grid = styled.div<{ columns: number | 'auto'; rows?: number; width: number; height: number }>`
    display: grid;
    grid-template-columns: ${props =>
        !props.columns || props.columns === 0
            ? `repeat(auto-fit, minmax(${props.width}px, 1fr))`
            : `repeat(${props.columns}, 1fr)`};
    ${props =>
        props.rows &&
        `
    grid-template-rows: repeat(${props.rows}, ${props.height}px);
  `}
    column-gap: 5px;
    row-gap: 5px;
    background-color: var(--gray-0);
    padding: 1px;
`

const GridItem = styled.div<{ height: number; width: number }>`
    background-color: var(--gray-100);
    padding: 20px;
    height: ${props => props.height}px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

const Label = styled.div<{
    color?: string
    fontSize?: number
    isBold?: boolean
}>`
    color: ${props => props.color || 'var(--gray-900)'};
    font-size: ${props => (props.fontSize ? `${props.fontSize}px` : '0.875rem')};
    font-weight: ${props => (props.isBold ? 'bold' : 'normal')};
    margin-bottom: 8px;
`

const Value = styled.div<{
    fontSize?: number
    isBold?: boolean
}>`
    color: var(--gray-1000);
    font-size: ${props => (props.fontSize ? `${props.fontSize}px` : '1.5rem')};
    font-weight: ${props => (props.isBold ? 'bold' : 'normal')};
    display: flex;
    align-items: baseline;
    gap: 4px;
`

const Unit = styled.span<{
    fontSize?: number
    isBold?: boolean
}>`
    color: var(--gray-900);
    font-size: ${props => (props.fontSize ? `${props.fontSize}px` : '0.875rem')};
    font-weight: ${props => (props.isBold ? 'bold' : 'normal')};
`

export default function TableGridComponent({ columns, rows, data }: GridProps) {
    const gridColumns = columns
    const [gridRows, setGridRows] = useState<number | undefined>(rows)
    const [gridDimensions, setGridDimensions] = useState({ width: 0, height: 0 })
    const [gridItemDimensions, setGridItemDimensions] = useState({
        height: 100,
        width: 250
    })
    const gridRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!gridRef.current || !containerRef.current) return

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect
                setGridDimensions({ width, height  })
            }
        })

        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    useEffect(() => {
        if (!gridRef.current) return

        let newHeight = 100
        let newWidth = 250

        if (rows) {
            newHeight = gridDimensions.height / rows
        }
        if (columns) {
            newWidth = gridDimensions.width / columns
        }

        setGridItemDimensions({ height: newHeight - 5, width: newWidth })

        const calculatedColumns = Math.floor(gridDimensions.width / newWidth)
        const calculatedRows = Math.ceil(data.length / calculatedColumns)
        setGridRows(calculatedRows)
    }, [gridDimensions, data.length, rows, columns])

    return (
        <GridContainer ref={containerRef} rows={rows}>
            <Grid
                ref={gridRef}
                columns={gridColumns}
                rows={gridRows}
                width={gridItemDimensions.width}
                height={gridItemDimensions.height}
            >
                {data.map((item, index) => (
                    <GridItem key={index} height={gridItemDimensions.height} width={gridItemDimensions.width}>
                        <Label color={item.color} fontSize={item.titleFontSize} isBold={item.titleBold}>
                            {item.title}
                        </Label>
                        <Value fontSize={item.valueFontSize} isBold={item.valueBold}>
                            {item.value}
                            <Unit fontSize={item.uomFontSize} isBold={item.uomBold}>
                                {item.__uom}
                            </Unit>
                        </Value>
                    </GridItem>
                ))}
            </Grid>
        </GridContainer>
    )
}
